import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleDot } from '@fortawesome/pro-solid-svg-icons';
import { faCircle } from '@fortawesome/pro-regular-svg-icons';
import { clsx } from 'clsx';
import { createContext, useContext, useId } from 'react';
import * as s from './radio.css';

type RadioGroupContext = {
  name: string;
  value: string | null;
  onChange: (value: string) => void;
  disabled: boolean;
};

const Ctx = createContext<RadioGroupContext | null>(null);

type RadioGroupProps = {
  value: string | null;
  onChange: (value: string) => void;
  /** Accessible name of the whole group, e.g. the question above it. */
  ariaLabel?: string;
  ariaLabelledBy?: string;
  disabled?: boolean;
  className?: string;
  children: React.ReactNode;
};

/** Native radios inside, so arrow keys move within the group for free. */
export const RadioGroup = ({
  value,
  onChange,
  ariaLabel,
  ariaLabelledBy,
  disabled = false,
  className,
  children,
}: RadioGroupProps) => {
  const name = useId();
  return (
    <Ctx.Provider value={{ name, value, onChange, disabled }}>
      <div
        role="radiogroup"
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        className={clsx(s.group, className)}
      >
        {children}
      </div>
    </Ctx.Provider>
  );
};

type RadioProps = {
  value: string;
  label: string;
  hint?: string;
  disabled?: boolean;
  testId?: string;
};

export const Radio = ({ value, label, hint, disabled, testId }: RadioProps) => {
  const group = useContext(Ctx);
  if (!group) throw new Error('<Radio> must be rendered inside <RadioGroup>');
  const isDisabled = disabled || group.disabled;
  const checked = group.value === value;
  const id = useId();

  return (
    <label
      htmlFor={id}
      className={s.option({ disabled: isDisabled })}
      data-testid={testId}
    >
      <input
        id={id}
        type="radio"
        name={group.name}
        value={value}
        className={s.input}
        checked={checked}
        disabled={isDisabled}
        onChange={() => group.onChange(value)}
      />
      <FontAwesomeIcon
        icon={checked ? faCircleDot : faCircle}
        className={s.icon({ checked })}
        aria-hidden
      />
      <span className={s.text}>
        <span className={s.title}>{label}</span>
        {hint && <span className={s.hint}>{hint}</span>}
      </span>
    </label>
  );
};
